/**
 * UUID / ULID inspector — pure, deterministic. Safe to render server-side.
 *
 * Validates and describes a UUID (RFC 4122/9562, versions 1–8) or a ULID,
 * extracting the embedded timestamp where one exists. Timestamps are derived
 * from the parsed identifier (never the current clock) and formatted as ISO
 * 8601 UTC, so output is deterministic.
 */

export type InspectResult =
  | {
      ok: true;
      kind: "uuid";
      normalized: string;
      version: number;
      versionLabel: string;
      variant: string;
      isNil: boolean;
      isMax: boolean;
      timestamp: string | null;
    }
  | { ok: true; kind: "ulid"; normalized: string; timestamp: string }
  | { ok: false; error: string };

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

const VERSION_LABELS: Record<string, string> = {
  "1": "Time-based (v1)",
  "2": "DCE security (v2)",
  "3": "Name-based, MD5 (v3)",
  "4": "Random (v4)",
  "5": "Name-based, SHA-1 (v5)",
  "6": "Reordered time (v6)",
  "7": "Unix time + random (v7)",
  "8": "Custom (v8)",
};

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
/** Unix ms for the Gregorian UUID epoch (1582-10-15). */
const GREGORIAN_OFFSET_MS = 12219292800000n;

function variantOf(nibble: number): string {
  if ((nibble & 0b1000) === 0) return "NCS (backward compatibility)";
  if ((nibble & 0b1100) === 0b1000) return "RFC 4122 / DCE 1.1";
  if ((nibble & 0b1110) === 0b1100) return "Microsoft (reserved)";
  return "Reserved (future)";
}

function inspectUuid(lower: string): InspectResult {
  const hex = lower.replace(/-/g, "");
  const isNil = hex === "0".repeat(32);
  const isMax = hex === "f".repeat(32);
  const version = Number.parseInt(hex[12], 16);
  const variant = variantOf(Number.parseInt(hex[16], 16));

  let timestamp: string | null = null;
  if (version === 1) {
    const timeLow = BigInt(`0x${hex.slice(0, 8)}`);
    const timeMid = BigInt(`0x${hex.slice(8, 12)}`);
    const timeHi = BigInt(`0x${hex.slice(12, 16)}`) & 0x0fffn;
    const ticks = (timeHi << 48n) | (timeMid << 32n) | timeLow;
    const ms = ticks / 10000n - GREGORIAN_OFFSET_MS;
    timestamp = new Date(Number(ms)).toISOString();
  } else if (version === 7) {
    const ms = Number.parseInt(hex.slice(0, 12), 16);
    timestamp = new Date(ms).toISOString();
  }

  const versionLabel = isNil
    ? "Nil UUID"
    : isMax
      ? "Max UUID"
      : Object.hasOwn(VERSION_LABELS, String(version))
        ? VERSION_LABELS[String(version)]
        : "Unknown version";

  return {
    ok: true,
    kind: "uuid",
    normalized: lower,
    version,
    versionLabel,
    variant,
    isNil,
    isMax,
    timestamp,
  };
}

function inspectUlid(upper: string): InspectResult {
  let ms = 0;
  for (const ch of upper.slice(0, 10)) ms = ms * 32 + CROCKFORD.indexOf(ch);
  return {
    ok: true,
    kind: "ulid",
    normalized: upper,
    timestamp: new Date(ms).toISOString(),
  };
}

export function inspectId(input: string): InspectResult {
  const s = input.trim();
  if (!s) return { ok: false, error: "Enter a UUID or ULID." };
  if (UUID_RE.test(s)) return inspectUuid(s.toLowerCase());
  if (ULID_RE.test(s)) return inspectUlid(s.toUpperCase());
  return {
    ok: false,
    error:
      "Not a valid UUID (8-4-4-4-12 hex) or ULID (26 Crockford base32 characters).",
  };
}
